/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Grid } from 'antd';

import { User } from '@/client';
import { PageHeader } from '@/components/PageHeader';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Avatar } from '@/primitives/Avatar';
import { Button } from '@/primitives/Button';
import { Col } from '@/primitives/Col';
import { Flex } from '@/primitives/Flex';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';

import './UserDetailsHeader.css';

export type UserDetailsHeaderProps = {
  user: User;
  openUpdateUserModal: (user: User) => void;
  setDeleteUserModalOpen: (open: boolean) => void;
};

const { useBreakpoint } = Grid;

export function UserDetailsHeader({ user, openUpdateUserModal, setDeleteUserModalOpen }: UserDetailsHeaderProps) {
  const { t } = useMimicTranslation('users');
  const screens = useBreakpoint();

  const buttonHeight = screens.md ? undefined : '45px';
  const buttonWidth = screens.md ? undefined : '50%';
  const buttonContainerWidth = screens.sm ? undefined : '100%';

  return (
    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ margin: 0 }}>
      <Col sm={24} md={12} style={{ padding: 0 }}>
        <PageHeader
          title={
            <Flex gap="xs" align="center">
              <Avatar
                dataTestId="user-avatar"
                text={user.displayName[0]}
                color="rgba(25, 25, 25, 0.65)"
                backgroundColor="rgba(25, 25, 25, 0.14)"
                size="lg"
              />
              <Flex vertical gap="none">
                <Text strong style={{ lineHeight: '32px', fontSize: '29px' }}>
                  {user.displayName}
                </Text>
                <Text type="subtle" size="md">
                  {user.email}
                </Text>
              </Flex>
            </Flex>
          }
        />
      </Col>
      <Col xs={24} sm={24} md={12} xl={10} xxl={6} style={{ padding: 0 }}>
        <div className="edit-delete-container" style={{ width: buttonContainerWidth }}>
          <Button
            type="default"
            onClick={() => setDeleteUserModalOpen(true)}
            dataTestId="delete-user-button"
            style={{ height: buttonHeight, width: buttonWidth }}
          >
            {t('actions.deleteUser')}
          </Button>
          <Button
            type="primary"
            onClick={() => openUpdateUserModal(user)}
            dataTestId="edit-user-button"
            style={{ height: buttonHeight, width: buttonWidth }}
          >
            {t('actions.editUser')}
          </Button>
        </div>
      </Col>
    </Row>
  );
}
